import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navigation from './Navigation';
import GoogleAuth from './GoogleAuth';
import { getApiUrl, isValidJWT } from '../utils/api';

export default function HeroSection() {
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    setIsAuthenticated(!!token);
  }, []);

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      setShowAuthModal(true);
    }
  };

  const handleGoogleSuccess = async (credential: string) => {
    setIsLoading(true);
    
    try {
      const response = await fetch(`${getApiUrl()}/auth/google`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ credential }),
      });

      if (response.ok) {
        const data = await response.json();

        // Validate token format before storing
        if (!data.token || !isValidJWT(data.token)) {
          console.error('Received invalid JWT token from server');
          setError('Authentication failed: Invalid token received');
          return;
        }

        localStorage.setItem('authToken', data.token);
        setIsAuthenticated(true);
        setShowAuthModal(false);
        navigate('/dashboard');
      } else {
        const errorData = await response.json();
        console.error('Authentication failed:', errorData.detail || 'Authentication failed');
      }
    } catch (err) {
      console.error('Network error occurred:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = (error: any) => {
    console.error('Google Auth Error:', error);
  };

  const handleViewDemo = () => {
    const demoSection = document.getElementById('demo-section');
    if (demoSection) {
      demoSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="relative min-h-screen bg-black text-white overflow-hidden">
      <Navigation />
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="h-full w-full bg-[linear-gradient(rgba(255,255,255,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.1)_1px,transparent_1px)] bg-[size:32px_32px]" />
      </div>
      
      {/* Subtle gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-gray-900/50 to-black"></div>

      <div className="relative z-10 container mx-auto px-6 py-20">
        <div className="flex flex-col items-center justify-center text-center max-w-5xl mx-auto min-h-[80vh]">
          
          {/* Main Headline */}
          <h1 className="text-4xl md:text-6xl font-bold leading-tight mb-8 text-white max-w-4xl">
            GPU-Powered Trading Strategies in Minutes
          </h1>

          {/* Subheadline */}
          <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl leading-relaxed">
            Build AI-driven quantitative strategies with GPU acceleration, ML frameworks, and transparent pricing.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-16">
            <button 
              onClick={handleGetStarted}
              className="px-8 py-4 bg-white hover:bg-gray-100 text-black font-medium rounded-lg transition-all duration-200"
            >
              {isAuthenticated ? 'Go to Dashboard' : 'Get Started'}
            </button>
            <button 
              onClick={handleViewDemo}
              className="px-8 py-4 bg-transparent border border-gray-700 hover:border-gray-600 text-gray-300 hover:text-white font-medium rounded-lg transition-all duration-200"
            >
              View Demo
            </button>
          </div>

          {/* Key Features */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 w-full max-w-3xl">
            <div className="flex flex-col items-center text-center">
              <h3 className="text-lg font-medium mb-2 text-white">GPU Acceleration</h3>
              <p className="text-gray-400 text-sm">10x faster backtesting & ML training</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <h3 className="text-lg font-medium mb-2 text-white">Pre-loaded Data</h3>
              <p className="text-gray-400 text-sm">5 years of market data</p>
            </div>

            <div className="flex flex-col items-center text-center">
              <h3 className="text-lg font-medium mb-2 text-white">Transparent Pricing</h3>
              <p className="text-gray-400 text-sm">Pay only for what you use</p>
            </div>
          </div>

        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      </div>

      {/* Authentication Modal */}
      {showAuthModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-gray-900 rounded-xl p-8 max-w-md w-full mx-4 border border-gray-800">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Get Started</h2>
              <button
                onClick={() => setShowAuthModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <p className="text-gray-400 mb-6">
              Sign in to access your PropAlgos dashboard and start building trading strategies.
            </p>

            <div className="space-y-4">
              <div className="flex flex-col items-center">
                {isLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                    <span className="ml-2 text-gray-400">Signing in...</span>
                  </div>
                ) : (
                  <GoogleAuth
                    onSuccess={handleGoogleSuccess}
                    onError={handleGoogleError}
                  />
                )}
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-700"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-gray-900 text-gray-400">or</span>
                </div>
              </div>

              <div className="text-center">
                <button 
                  onClick={() => navigate('/login')}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  More sign-in options
                </button>
              </div>
            </div>

            <div className="mt-6 text-center text-xs text-gray-500">
              By signing in, you agree to our{' '}
              <Link to="/terms" className="text-gray-400 hover:text-white transition-colors underline">
                Terms of Service
              </Link>{' '}
              and{' '}
              <Link to="/privacy" className="text-gray-400 hover:text-white transition-colors underline">
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}